/**
 * Countdown timer.
 *
 * EK-060 / EK-061 (Oct 2026). Last year's timer issue had two causes:
 *   1. The end date was parsed with `new Date('2025-12-31T23:59:59')`. A date without an offset is
 *      read in the visitor's own timezone, so the deal ended at a different moment in every market.
 *   2. The markup shipped with "00H 00M 00S" until the script ran, so every page load and every
 *      navigation showed a reset timer for a moment.
 * Dates are now read in one fixed timezone (data-timezone, an IANA name such as Europe/Amsterdam),
 * so every market counts down to the same moment, and the digits stay invisible (their space is
 * kept, so nothing moves) until the first real value is written.
 *
 * Attributes
 *   data-end-date          YYYY-MM-DDTHH:MM(:SS). An explicit offset or Z is respected as is.
 *   data-start-date        Optional. Before this moment the hide target is hidden.
 *   data-timezone          Optional IANA timezone. Without it the old behaviour (visitor time) stays.
 *   data-ended             "text" (default, old behaviour) shows data-replacement-text,
 *                          "hide" hides the hide target.
 *   data-hide-target       Optional selector for closest(); defaults to the element itself.
 *   data-format            "legacy" (default: DAYS above the threshold, then 00H 00M 00S),
 *                          "units" (2D 14H 09M, then 14H 09M 33S) or "blocks": the countdown
 *                          holds one [data-unit="d|h|m|s"] per unit with a [data-unit-value]
 *                          inside, and gets data-has-days while a day or more is left, so the
 *                          days box only shows when it is needed (EK-060 design, 27 Oct).
 *   data-switch-threshold  Hours, legacy format only (default 48).
 *
 * Preview: ?deal_preview=1 on any URL, or the theme editor, shows banners before their start
 * date, so a future day deal can be reviewed with its real countdown.
 */
(() => {
  if (customElements.get('countdown-timer')) return;

  const OFFSET_PATTERN = /(Z|[+-]\d{2}:?\d{2})$/i;
  const LOCAL_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/;
  const formatters = new Map();

  const formatterFor = (timeZone) => {
    if (!formatters.has(timeZone)) {
      formatters.set(
        timeZone,
        new Intl.DateTimeFormat('en-US', {
          timeZone,
          hourCycle: 'h23',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })
      );
    }
    return formatters.get(timeZone);
  };

  // Offset of a timezone at a given UTC moment, in milliseconds.
  const offsetAt = (timeZone, utcMs) => {
    const parts = {};
    formatterFor(timeZone)
      .formatToParts(new Date(utcMs))
      .forEach((part) => {
        parts[part.type] = part.value;
      });
    const asUtc = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour) % 24,
      Number(parts.minute),
      Number(parts.second)
    );
    return asUtc - (utcMs - (utcMs % 1000));
  };

  /**
   * Wall clock time in a timezone to a UTC timestamp. Two passes, so the result is right on both
   * sides of a daylight saving change.
   */
  const parseDate = (value, timeZone) => {
    if (!value) return NaN;
    const text = String(value).trim();
    if (OFFSET_PATTERN.test(text) || !timeZone) return new Date(text).getTime();
    const match = text.match(LOCAL_PATTERN);
    if (!match) return new Date(text).getTime();
    const wall = Date.UTC(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      Number(match[4]),
      Number(match[5]),
      Number(match[6] || 0)
    );
    try {
      let utc = wall - offsetAt(timeZone, wall);
      utc = wall - offsetAt(timeZone, utc);
      return utc;
    } catch (error) {
      // Unknown timezone name: fall back to the visitor's time rather than breaking the page.
      return new Date(text).getTime();
    }
  };

  const isPreview = () => {
    if (window.Shopify && window.Shopify.designMode) return true;
    try {
      return new URLSearchParams(window.location.search).get('deal_preview') === '1';
    } catch (error) {
      return false;
    }
  };

  const pad = (number) => String(number).padStart(2, '0');

  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      const timeZone = this.getAttribute('data-timezone') || '';
      this.endDate = parseDate(this.getAttribute('data-end-date'), timeZone);
      this.startDate = parseDate(this.getAttribute('data-start-date'), timeZone);
      if (isNaN(this.endDate)) return;

      this.preview = isPreview();
      this.endedMode = this.getAttribute('data-ended') || 'text';
      this.format = this.getAttribute('data-format') || 'legacy';
      this.replacementText = this.getAttribute('data-replacement-text') || '';
      this.textElement = this.querySelector('[data-countdown-text]');
      this.countdownElement = this.querySelector('[data-countdown]');
      const hideSelector = this.getAttribute('data-hide-target');
      this.hideTarget = (hideSelector && this.closest(hideSelector)) || this;

      // Threshold in hours - when to switch from days to hours/minutes/seconds
      // Default: 48 hours (2 days)
      this.switchThreshold = parseInt(this.getAttribute('data-switch-threshold') || '48', 10);

      this.updateCountdown();
      if (this.interval) clearInterval(this.interval);
      this.interval = setInterval(() => this.updateCountdown(), 1000);
    }

    setVisible(visible) {
      if (visible) {
        this.hideTarget.removeAttribute('hidden');
      } else {
        this.hideTarget.setAttribute('hidden', '');
      }
      this.hideTarget.dispatchEvent(new CustomEvent('countdown:visibility', { bubbles: true, detail: { visible } }));
    }

    updateCountdown() {
      const now = Date.now();

      if (!this.preview && !isNaN(this.startDate) && now < this.startDate) {
        this.setVisible(false);
        return;
      }

      const distance = this.endDate - now;

      if (distance < 0) {
        clearInterval(this.interval);
        if (this.endedMode === 'hide' && !this.preview) {
          this.setVisible(false);
          return;
        }
        if (this.textElement && this.replacementText) {
          this.textElement.innerHTML = this.replacementText;
        }
        if (this.countdownElement) {
          this.countdownElement.textContent = '';
        }
        this.setAttribute('data-countdown-ended', '');
        this.setAttribute('data-ready', '');
        this.setVisible(true);
        return;
      }

      if (this.hideTarget.hasAttribute('hidden')) this.setVisible(true);
      if (!this.countdownElement) {
        this.setAttribute('data-ready', '');
        return;
      }

      const totalHours = Math.floor(distance / (1000 * 60 * 60));
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      if (this.format === 'blocks') {
        const values = { d: days, h: hours, m: minutes, s: seconds };
        this.countdownElement.querySelectorAll('[data-unit]').forEach((unit) => {
          const value = unit.querySelector('[data-unit-value]');
          const text = pad(values[unit.getAttribute('data-unit')] || 0);
          if (value && value.textContent !== text) value.textContent = text;
        });
        this.countdownElement.toggleAttribute('data-has-days', days > 0);
      } else if (this.format === 'units') {
        this.countdownElement.textContent =
          days > 0 ? `${days}D ${pad(hours)}H ${pad(minutes)}M` : `${pad(hours)}H ${pad(minutes)}M ${pad(seconds)}S`;
      } else if (totalHours >= this.switchThreshold) {
        // Show days if we're above the threshold
        const dayText = days === 1 ? 'DAY' : 'DAYS';
        this.countdownElement.textContent = `${days} ${dayText}`;
      } else {
        // Show hours, minutes, and seconds when below threshold
        this.countdownElement.textContent = `${pad(totalHours)}H ${pad(minutes)}M ${pad(seconds)}S`;
      }
      this.setAttribute('data-ready', '');
    }

    disconnectedCallback() {
      if (this.interval) {
        clearInterval(this.interval);
      }
    }
  }

  window.EksterCountdown = { parseDate, isPreview };
  customElements.define('countdown-timer', CountdownTimer);
})();
